import { useCallback, useState } from 'react';
import type { BackupDownload, ServerBackupsResponse, ServerView } from '@hearth/shared';import { ApiError, type Api } from '../api';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useCached } from './cache';
import { cardKind, errorText } from './serverWords';

// The Backups tab (M10.14, owners): a backup is taken each time the server falls asleep; restoring
// one replaces the world at the next wake. Only while it's asleep; a pending restore can be cancelled.
// Any backup can be downloaded, in any state: the API signs a link, the browser saves the file.

type Backup = ServerBackupsResponse['backups'][number];

/** "Today, 21:40", "Yesterday, 23:05", "18 October, 22:30" (local time). */
export function backupWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(at)) / 86_400_000);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  const date = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', ...(at.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
  return `${date}, ${time}`;
}

/** "412 MB", "1.2 GB". */
export function backupSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

/** What restoring asks: a warning when the last sleep may not have saved the world. */
export function restoreText(server: ServerView, when: string): string {
  const unclean = server.lastStopClean === false ? ' Its last sleep may not have saved the world, so what was played since then could already be lost.' : '';
  // "Today, 21:40" → "today at 21:40"; "18 October, 22:30" → "on 18 October at 22:30".
  const [day, time] = when.split(', ');
  const at = day === 'Today' || day === 'Yesterday' ? `${day.toLowerCase()} at ${time}` : `on ${day} at ${time}`;
  return `Next time ${server.name} wakes, its world goes back to how it was ${at}. Backups stay in this list.${unclean}`;
}

/**
 * Downloads a backup: the API signs a link, saved as an attachment, so the page (or dialog) stays where
 * it is while the file downloads. The Backups tab and the destroy dialog both use it.
 */
export async function downloadBackup(api: Api, serverId: string, backupId: string): Promise<void> {
  const { url } = await api.get<BackupDownload>(`/v1/servers/${encodeURIComponent(serverId)}/backups/${encodeURIComponent(backupId)}/download`);
  window.location.assign(url);
}

/** A backup's Download button (the Backups tab's and the destroy dialog's): the icon, then "Starting…" while the link is fetched. */
export function DownloadButton({ starting, disabled, label, onClick }: { starting: boolean; disabled: boolean; label: string; onClick: () => void }) {
  return (
    <button className="btn btn-small" type="button" disabled={disabled} aria-label={label} onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 4v11" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 20h14" />
      </svg>
      {starting ? 'Starting…' : 'Download'}
    </button>
  );
}

export function BackupsTab({ api, server, onChanged, onGone }: { api: Api; server: ServerView; onChanged: () => Promise<void>; onGone: () => void }) {
  const [asking, setAsking] = useState<Backup>();
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState<string>();
  const [error, setError] = useState('');
  const base = `/v1/servers/${encodeURIComponent(server.serverId)}`;

  const refused = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) onGone();
      else setError(errorText(err));
    },
    [onGone],
  );

  // Kept between tabs (cache.ts); read again when it's old, a sleep adds a backup, or a restore is asked for or done.
  const backups = useCached(`backups:${server.serverId}`, async () => (await api.get<ServerBackupsResponse>(`${base}/backups`)).backups, {
    version: `${server.lastBackupAt ?? ''} ${server.restorePending}`,
    failed: refused,
  }).data;

  async function send(change: () => Promise<unknown>) {
    setBusy(true);
    try {
      await change();
      setError('');
      await onChanged();
    } catch (err) {
      refused(err);
    } finally {
      setBusy(false);
      setAsking(undefined);
    }
  }

  async function download(backup: Backup) {
    setDownloading(backup.id);
    try {
      await downloadBackup(api, server.serverId, backup.id);
      setError('');
    } catch (err) {
      refused(err);
    } finally {
      setDownloading(undefined);
    }
  }

  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      <BackupsPanel
        server={server}
        backups={backups}
        now={new Date()}
        busy={busy}
        downloading={downloading}
        onDownload={(b) => void download(b)}
        onRestore={setAsking}
        onCancel={() => void send(() => api.delete(`${base}/restore`))}
      />
      <ConfirmDialog
        open={!!asking}
        title="Restore this backup?"
        confirmLabel="Restore"
        tone="danger"
        busy={busy}
        // An unclean last sleep: the API refuses unless forced, and the question already warned.
        onConfirm={() => asking && void send(() => api.post(`${base}/restore`, { key: asking.id, ...(server.lastStopClean === false ? { force: true } : {}) }))}
        onCancel={() => setAsking(undefined)}
      >
        {asking ? restoreText(server, backupWhen(asking.takenAt, new Date())) : ''}
      </ConfirmDialog>
    </>
  );
}

/** The backups panel, given what's loaded (no fetching: easy to test). `backups` undefined = loading. */
export function BackupsPanel(props: {
  server: ServerView;
  backups: Backup[] | undefined;
  now: Date;
  busy: boolean;
  /** The backup whose link is being fetched, if any. */
  downloading?: string | undefined;
  onDownload: (backup: Backup) => void;
  onRestore: (backup: Backup) => void;
  onCancel: () => void;
}) {
  const { server, backups, now, busy, downloading, onDownload, onRestore, onCancel } = props;
  const asleep = cardKind(server) === 'asleep' && server.status !== 'DESTROYED';
  return (
    <section className="panel panel-backups">
      <h2>Backups</h2>
      <p className="quiet">Taken every time it falls asleep. Restoring replaces the world as it is now. Download one to keep a copy, or to play it in single player.</p>
      {server.restorePending && (
        <div className="restore-pending" role="status">
          <span>A restore is waiting: it happens when {server.name} next wakes.</span>
          {asleep && (
            <button className="quiet-btn" type="button" disabled={busy} onClick={onCancel}>Cancel restore</button>
          )}
        </div>
      )}
      {!asleep && server.status !== 'DESTROYED' && backups?.length ? <p className="quiet">Put it to sleep to restore a backup.</p> : null}
      {!backups ? (
        <p className="quiet" aria-live="polite">Loading…</p>
      ) : backups.length === 0 ? (
        <p className="quiet">No backups yet: one is taken each time it falls asleep.</p>
      ) : (
        <ul className="backups">
          {backups.map((b, i) => (
            <li key={b.id}>
              <span className="backup-when">
                {backupWhen(b.takenAt, now)}
                {i === 0 && <span className="quiet"> · Latest</span>}
              </span>
              <span className="quiet backup-size">{backupSize(b.bytes)}</span>
              <span className="backup-actions">
                <DownloadButton
                  starting={downloading === b.id}
                  disabled={downloading !== undefined}
                  label={`Download the backup from ${backupWhen(b.takenAt, now)}`}
                  onClick={() => onDownload(b)}
                />
                {asleep && (
                  <button className="btn btn-small" type="button" disabled={busy} aria-label={`Restore the backup from ${backupWhen(b.takenAt, now)}`} onClick={() => onRestore(b)}>
                    Restore
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
